import { useLiveQuery } from 'dexie-react-hooks'
import { conflictosAbiertos, solicitudesPendientes } from '../datos/consultas.js'
import { db } from '../datos/db.js'
import { encolar } from '../datos/sync.js'
import { fechaCorta, hora } from '../datos/formato.js'
import { TituloPagina } from '../comunes/AppShell.js'
import { Boton } from '../comunes/Boton.js'

/**
 * Bandeja de recepción (T11).
 *
 * Dos colas que en la práctica se atienden juntas, aunque vengan de lados
 * opuestos:
 *
 *   solicitudes  lo que pidió gente desde la web (`origen='publico'`,
 *                `estado='pendiente'`). Se confirma o se rechaza en un clic.
 *   conflictos   lo que se escribió sin conexión y perdió su slot al subir
 *                (§5.3). Acá no hay resolución automática a propósito: decide
 *                una persona, con el horario original a la vista.
 */
/** Un ícono, una línea, nada más. Un rectángulo vacío de 800 px no dice nada. */
function BandejaVacia() {
  return (
    <div className="flex flex-col items-center py-16 text-center">
      <svg
        viewBox="0 0 24 24"
        aria-hidden="true"
        className="h-10 w-10 text-slate-300"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.5}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M3 13h4l2 3h6l2-3h4M3 13l2-7h14l2 7M3 13v5a1 1 0 001 1h16a1 1 0 001-1v-5"
        />
      </svg>
      <p className="mt-3 text-sm text-slate-500">No hay nada esperando.</p>
    </div>
  )
}

export interface BandejaProps {
  onVerEnAgenda?: ((citaId: string) => void) | undefined
  /**
   * Dentro de «Hoy» no dibuja título ni estado vacío: la franja que la contiene
   * ya dijo cuántas cosas hay, y sólo aparece cuando hay alguna.
   */
  compacta?: boolean
}

export function Bandeja({ onVerEnAgenda, compacta = false }: BandejaProps) {
  const solicitudes = useLiveQuery(() => solicitudesPendientes(), [], [])
  const conflictos = useLiveQuery(() => conflictosAbiertos(), [], [])

  async function resolver(citaId: string, estado: 'confirmada' | 'cancelada') {
    // Optimista: la fila desaparece de la bandeja en el acto y la mutación
    // viaja cuando haya red. Confirmar un turno no puede quedar esperando.
    await db.cita.update(citaId, { estado })
    await encolar('cambiar_estado', { citaId, estado })
  }

  const vacia = solicitudes.length === 0 && conflictos.length === 0

  return (
    <div>
      {!compacta && (
        <TituloPagina
          titulo="Bandeja"
          bajada="Solicitudes de la web y escrituras que quedaron sin lugar."
        />
      )}

      {vacia && !compacta && <BandejaVacia />}

      {conflictos.length > 0 && (
        <section className="mb-8">
          <h2 className="mb-2 text-[13px] font-medium text-rose-800">
            Sin lugar ({conflictos.length})
          </h2>
          <ul className="space-y-2">
            {conflictos.map((c) => (
              <li
                key={c.mutationId}
                className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm"
              >
                <p className="text-rose-900">{c.mensaje}</p>
                <p className="mt-1 text-xs text-rose-700">
                  Se intentó {fechaCorta(c.detectadoEn)} a las {hora(c.detectadoEn)}
                </p>
                <Boton
                  variante="danger"
                  tamano="sm"
                  className="mt-2"
                  onClick={() => void db.conflictos.update(c.mutationId, { resuelto: true })}
                >
                  Ya lo resolví
                </Boton>
              </li>
            ))}
          </ul>
        </section>
      )}

      {solicitudes.length > 0 && (
        <section>
          <h2 className="mb-2 text-[13px] font-medium text-slate-600">
            Solicitudes de la web ({solicitudes.length})
          </h2>
          <ul className="space-y-2">
            {solicitudes.map(({ cita, tramo, cliente }) => (
              <li key={cita.id} className="rounded-lg border border-slate-200 p-3">
                <div className="flex flex-wrap items-baseline gap-x-3">
                  <span className="font-medium text-slate-900">
                    {cliente?.nombre ?? 'Sin nombre'}
                  </span>
                  <span className="text-sm text-slate-500">{cliente?.telefono ?? ''}</span>
                </div>
                <p className="mt-1 text-sm text-slate-700">
                  {fechaCorta(tramo.inicio)} · {hora(tramo.inicio)}–{hora(tramo.fin)}
                </p>
                {cita.notas && <p className="mt-1 text-sm text-slate-500">{cita.notas}</p>}

                {/* Un clic para confirmar: es el criterio de aceptación de T11. */}
                <div className="mt-3 flex gap-2">
                  <Boton variante="primary" onClick={() => void resolver(cita.id, 'confirmada')}>
                    Confirmar
                  </Boton>
                  <Boton variante="secondary" onClick={() => void resolver(cita.id, 'cancelada')}>
                    Rechazar
                  </Boton>
                  {onVerEnAgenda && (
                    <Boton variante="ghost" onClick={() => onVerEnAgenda(cita.id)}>
                      Ver en la agenda
                    </Boton>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  )
}
